import produce from "immer"
import { IDLE } from "const"
import {
    CcReviewComplianceHistoryCreate,
    CcReviewComplianceItemState,
} from "server/models"
import { InferValueTypes } from "client/utils"
import { initCcLibraryItem } from "client/store"
import * as types from "./types"
import * as actions from "./actions"

type ActionTypes = ReturnType<InferValueTypes<typeof actions>>

export const initReviewComplianceItemState: CcReviewComplianceItemState = {
    itemId: null,
    reviewComplianceRevisionId: null,
    levelId: null,
    verifierId: null,
    settings: null,
}

export const initReviewComplianceHistory: CcReviewComplianceHistoryCreate = {
    itemId: null,
    reviewComplianceRevisionId: null,
    parentId: null,
    prevLevelId: null,
    prevUserId: null,
    nextLevelId: null,
    nextUserId: null,
    responseId: null,
    answer: null,
    remark: null,
    prevStatus: null,
    nextStatus: null,
    workflow: null,
    systemMessage: true,
    editorId: null,
}

const initCcReviewComplianceState: types.CcReviewComplianceState = {
    // list
    formalTableClass: "formal-review-compliance-table",
    reviewTableClass: "review-compliance-table",

    showFormalColumnSettings: false,
    showReviewColumnSettings: false,

    itemList: {},
    itemGroupedIds: {},

    setList: {},
    setIds: [],

    revisionList: {},
    revisionActiveList: {},

    itemStateList: {},
    itemHistoryList: {},
    tempAnswerList: {},

    // selected items
    formalSelectedItemIds: [],
    formalVisibleItemIds: [],
    reviewSelectedItemIds: [],
    reviewVisibleItemIds: [],

    setLoadState: IDLE,
    itemLoadState: IDLE,

    // popup menu
    active: null,
    menuShow: false,
    menuTarget: null,

    // view history
    itemDetails: initCcLibraryItem,
    itemState: initReviewComplianceItemState,
    itemHistory: [],
    tempAnswers: {},
    tempAnswerVersions: {},
    tempAnswerHistory: null,
    reloadAnswer: true,
}

const ccReviewComplianceReducer = produce((
    draft: types.CcReviewComplianceState = initCcReviewComplianceState,
    action: ActionTypes = null,
): types.CcReviewComplianceState => {
    switch (action.type) {
        // list
        case types.CC_REVIEW_COMPLIANCE_FORMAL_COLUMN_SETTINGS:
            draft.showFormalColumnSettings = action.show
            return draft

        case types.CC_REVIEW_COMPLIANCE_COLUMN_SETTINGS:
            draft.showReviewColumnSettings = action.show
            return draft

        case types.CC_REVIEW_COMPLIANCE_ITEM_LIST:
            draft.itemList = {
                ...draft.itemList,
                ...action.itemList,
            }
            return draft

        case types.CC_REVIEW_COMPLIANCE_ITEM_GROUPED_IDS:
            draft.itemGroupedIds = {
                ...draft.itemGroupedIds,
                [action.setId]: action.groupedIds,
            }
            return draft

        case types.CC_REVIEW_COMPLIANCE_CLEAR_ITEM_GROUPED_IDS:
            draft.itemGroupedIds = {}
            return draft

        case types.CC_REVIEW_COMPLIANCE_SET_LIST:
            draft.setList = action.setList
            return draft

        case types.CC_REVIEW_COMPLIANCE_SET_IDS:
            draft.setIds = action.setIds
            return draft

        case types.CC_REVIEW_COMPLIANCE_REVISION_LIST:
            draft.revisionList = {
                ...draft.revisionList,
                [action.setId]: action.revisions,
            }
            return draft

        case types.CC_REVIEW_COMPLIANCE_REVISION_ACTIVE_LIST:
            draft.revisionActiveList = {
                ...draft.revisionActiveList,
                [action.versionId]: {
                    ...draft.revisionActiveList[action.versionId],
                    [action.setId]: action.revision,
                },
            }
            return draft

        case types.CC_REVIEW_COMPLIANCE_ITEM_STATE_LIST:
            draft.itemStateList = {
                ...draft.itemStateList,
                [action.setId]: action.itemStateList,
            }
            return draft

        case types.CC_REVIEW_COMPLIANCE_ITEM_HISTORY_LIST:
            draft.itemHistoryList = {
                ...draft.itemHistoryList,
                [action.setId]: action.itemHistoryList,
            }
            return draft

        case types.CC_REVIEW_COMPLIANCE_TEMP_ANSWER_LIST:
            draft.tempAnswerList = {
                ...draft.tempAnswerList,
                [action.setId]: action.tempAnswerList,
            }
            return draft

        // selected items
        case types.CC_REVIEW_COMPLIANCE_FORMAL_SELECTED_ITEM_IDS:
            draft.formalSelectedItemIds = action.selected
            return draft

        case types.CC_REVIEW_COMPLIANCE_FORMAL_VISIBLE_ITEM_IDS:
            draft.formalVisibleItemIds = action.visible
            return draft

        case types.CC_REVIEW_COMPLIANCE_SELECTED_ITEM_IDS:
            draft.reviewSelectedItemIds = action.selected
            return draft

        case types.CC_REVIEW_COMPLIANCE_VISIBLE_ITEM_IDS:
            draft.reviewVisibleItemIds = action.visible
            return draft


        case types.CC_REVIEW_COMPLIANCE_SET_LOAD_STATE:
            draft.setLoadState = action.state
            return draft

        case types.CC_REVIEW_COMPLIANCE_ITEM_LOAD_STATE:
            draft.itemLoadState = action.state
            return draft

        // popup menu
        case types.CC_REVIEW_COMPLIANCE_ACTIVE:
            draft.active = action.active
            return draft

        case types.CC_REVIEW_COMPLIANCE_POPUP_MENU_SHOW:
            draft.menuShow = action.show
            return draft

        case types.CC_REVIEW_COMPLIANCE_POPUP_MENU_TARGET:
            draft.menuTarget = action.target
            return draft

        // view history
        case types.CC_REVIEW_COMPLIANCE_ITEM_DETAILS:
            draft.itemDetails = action.details
            return draft

        case types.CC_REVIEW_COMPLIANCE_ITEM_STATE:
            draft.itemState = action.state
            return draft

        case types.CC_REVIEW_COMPLIANCE_ITEM_HISTORY:
            draft.itemHistory = action.history
            return draft

        case types.CC_REVIEW_COMPLIANCE_TEMP_ANSWERS:
            draft.tempAnswers = action.answers
            return draft

        case types.CC_REVIEW_COMPLIANCE_TEMP_ANSWER_VERSIONS:
            draft.tempAnswerVersions = action.versions
            return draft

        case types.CC_REVIEW_COMPLIANCE_TEMP_ANSWER_HISTORY:
            draft.tempAnswerHistory = action.history
            return draft

        case types.CC_REVIEW_COMPLIANCE_RELOAD_ANSWER:
            draft.reloadAnswer = action.value
            return draft

        default:
            return draft
    }
})
export default ccReviewComplianceReducer
